import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Badge de statut (components/Badge) : un mot, une teinte ET une forme dessinée
 * devant le mot, pour que le statut ne repose jamais sur la couleur seule.
 * success = disque, warning = triangle, danger = losange, info = carré,
 * neutral = anneau.
 */
const badgeVariants = cva(
  [
    'inline-flex h-[22px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-xs font-medium',
    "before:block before:size-2 before:shrink-0 before:rounded-full before:bg-current before:content-['']",
  ].join(' '),
  {
    variants: {
      variant: {
        success: 'bg-success-bg text-success-fg',
        warning:
          'bg-warning-bg text-warning-fg before:rounded-none before:[clip-path:polygon(50%_0,100%_100%,0_100%)]',
        danger:
          'bg-danger-bg text-danger-fg before:rounded-none before:[clip-path:polygon(50%_0,100%_50%,50%_100%,0_50%)]',
        info: 'bg-info-bg text-info-fg before:rounded-[1px]',
        neutral:
          'bg-muted text-muted-foreground before:border-[1.5px] before:border-current before:bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps): React.JSX.Element {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
